import { Spinner } from "@fluentui/react-components";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { ErrorState } from "@/components/ErrorState";
import {
  getTeamsContext,
  initializeTeams,
  notifyTeamsSuccess,
  onThemeChange,
  type TeamsContextSnapshot,
  type TeamsTheme,
} from "@/lib/teamsClient";

interface TeamsAuthContextValue {
  inTeams: boolean;
  theme: TeamsTheme;
  context: TeamsContextSnapshot | null;
}

const TeamsAuthContext = createContext<TeamsAuthContextValue | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useTeamsAuth(): TeamsAuthContextValue {
  const ctx = useContext(TeamsAuthContext);
  if (!ctx) throw new Error("useTeamsAuth must be used within TeamsAuthProvider");
  return ctx;
}

export function TeamsAuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<
    | { phase: "loading" }
    | { phase: "ready"; value: TeamsAuthContextValue }
    | { phase: "error"; message: string }
  >({ phase: "loading" });

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const inTeams = await initializeTeams();
        const context = await getTeamsContext();
        if (!active) return;
        setState({
          phase: "ready",
          value: {
            inTeams,
            theme: context?.theme ?? "default",
            context,
          },
        });
        if (inTeams) notifyTeamsSuccess();
      } catch (err) {
        if (!active) return;
        setState({
          phase: "error",
          message: err instanceof Error ? err.message : "Teams init failed",
        });
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    onThemeChange((theme) =>
      setState((s) =>
        s.phase === "ready" ? { ...s, value: { ...s.value, theme } } : s,
      ),
    );
  }, []);

  const value = useMemo(
    () => (state.phase === "ready" ? state.value : null),
    [state],
  );

  if (state.phase === "loading") {
    return (
      <div style={{ padding: 32 }}>
        <Spinner label="Connecting to Teams…" />
      </div>
    );
  }
  if (state.phase === "error" || !value) {
    return (
      <ErrorState
        title="Could not initialize Teams"
        message={state.phase === "error" ? state.message : "Unknown error"}
      />
    );
  }
  return (
    <TeamsAuthContext.Provider value={value}>
      {children}
    </TeamsAuthContext.Provider>
  );
}
